import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { attractionBookings } from "./service";
import useTableSearch from "../../../../hooks/useTableSearch";
import Dialog from "../../../dialog/Dialog";
import Pagination from "../../../common/Pagination";
import {
  AdminPageCard,
  AdminPageCardToolbar,
} from "../../../common/AdminPageLayout";
import {
  AdminTable,
  AdminTableHead,
  AdminTableTh,
  AdminTableBody,
  AdminTableRow,
  AdminTableCell,
  TableSearchInput,
  TableEmptyRow,
  ViewDetailsButton,
  TableActionsCell,
} from "../../../common/adminTable";
import {
  BookingDetailHeader,
  BookingDetailSection,
  InfoRow,
} from "../BookingDetailUi";
import {
  formatListingDate as formatDate,
  formatListingTime as formatTime,
} from "../../../../utils/formatListingDate";

const AttractionBookings = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const { searchQuery, setSearchQuery, activeSearch, clearSearch } =
    useTableSearch();
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSearch]);

  const { data: bookingsData } = useQuery({
    queryKey: ["attractionBookingsAdmin", currentPage, activeSearch],
    queryFn: () => attractionBookings(currentPage, activeSearch),
  });

  const bookingsList = bookingsData?.data?.data || [];
  const pagination = {
    currentPage: bookingsData?.data?.current_page || 1,
    lastPage: bookingsData?.data?.last_page || 1,
    perPage: bookingsData?.data?.per_page || 10,
    total: bookingsData?.data?.total || 0,
    from: bookingsData?.data?.from || 0,
    to: bookingsData?.data?.to || 0,
  };

  const handlePageChange = (page) => {
    if (page !== currentPage) setCurrentPage(page);
  };

  const handleViewDetails = (booking) => {
    setSelectedBookingId(booking.id);
    setShowDetailsDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDetailsDialog(false);
    setSelectedBookingId(null);
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    });
  };

  const getPaymentStatus = (booking) => {
    const balance = parseFloat(booking.balance || 0);
    const paid = parseFloat(booking.total_paid || 0);
    if (balance === 0 && paid > 0) return "Paid";
    if (paid > 0) return "Partially Paid";
    return "Unpaid";
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case "Paid":
        return "bg-green-100 text-green-800";
      case "Partially Paid":
        return "bg-yellow-100 text-yellow-800";
      case "Unpaid":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const selectedBooking = bookingsList.find((b) => b.id === selectedBookingId);
  const selectedBookingTickets =
    selectedBooking?.attraction_booking_tickets ||
    selectedBooking?.attractionBookingTickets ||
    [];
  const selectedBookingPayment =
    selectedBooking?.attraction_payment || selectedBooking?.attractionPayment;

  const safeText = (value, fallback = "N/A") => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === "string" && value.trim() === "") return fallback;
    return String(value);
  };

  return (
    <>
      <AdminPageCard>
        <AdminPageCardToolbar>
          <TableSearchInput
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            placeholder="Search attraction bookings..."
          />
        </AdminPageCardToolbar>

        <AdminTable className="shadow-none rounded-none">
        <AdminTableHead>
          <AdminTableTh>#</AdminTableTh>
          <AdminTableTh>Attraction</AdminTableTh>
          <AdminTableTh>Customer</AdminTableTh>
          <AdminTableTh>Visit Date</AdminTableTh>
          <AdminTableTh>Total</AdminTableTh>
          <AdminTableTh>Paid</AdminTableTh>
          <AdminTableTh>Balance</AdminTableTh>
          <AdminTableTh>Status</AdminTableTh>
          <AdminTableTh align="right">Actions</AdminTableTh>
        </AdminTableHead>
        <AdminTableBody>
          {bookingsList.length > 0 ? (
            bookingsList.map((booking, idx) => (
              <AdminTableRow key={booking.id}>
                <AdminTableCell>
                  {(pagination.currentPage - 1) * pagination.perPage + idx + 1}
                </AdminTableCell>
                <AdminTableCell>{booking.attraction?.name || "Attraction"}</AdminTableCell>
                <AdminTableCell>{booking.user?.name || "Customer"}</AdminTableCell>
                <AdminTableCell>
                  {formatDate(booking.visit_date || booking.created_at)}
                </AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.total_amount)}</AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.total_paid)}</AdminTableCell>
                <AdminTableCell>{formatCurrency(booking.balance)}</AdminTableCell>
                <AdminTableCell>
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getPaymentStatusColor(
                      getPaymentStatus(booking)
                    )}`}
                  >
                    {getPaymentStatus(booking)}
                  </span>
                </AdminTableCell>
                <TableActionsCell>
                  <ViewDetailsButton onClick={() => handleViewDetails(booking)} />
                </TableActionsCell>
              </AdminTableRow>
            ))
          ) : (
            <TableEmptyRow
              colSpan={9}
              searchQuery={activeSearch}
              onClearSearch={clearSearch}
              emptyMessage="No attraction bookings found"
              searchMessage="No attraction bookings found matching your search"
            />
          )}
        </AdminTableBody>
        </AdminTable>
      </AdminPageCard>

      {/* Details Dialog */}
      {showDetailsDialog && selectedBooking && (
        <Dialog
          title="Attraction Booking Details"
          open={showDetailsDialog}
          onClose={handleCloseDialog}
          width="w-3/4"
          showCloseButton={true}
        >
          <div className="space-y-4">
            <BookingDetailHeader
              bookingId={selectedBooking.id}
              title={selectedBooking.attraction?.name || "Attraction"}
              meta={
                <>
                  <span className="inline-flex items-center gap-1">
                    <i className="fi fi-rr-calendar text-[10px] text-gray-400" />
                    {formatDate(selectedBooking.visit_date)}
                  </span>
                  <span className="text-gray-300">·</span>
                  <span className="inline-flex items-center gap-1">
                    <i className="fi fi-rr-marker text-[10px] text-gray-400" />
                    {selectedBooking.attraction?.location || "N/A"}
                  </span>
                </>
              }
              status={getPaymentStatus(selectedBooking)}
              statusClassName={getPaymentStatusColor(getPaymentStatus(selectedBooking))}
              amount={formatCurrency(selectedBooking.total_amount)}
              bookedAt={formatDate(selectedBooking.created_at)}
              bookedTime={formatTime(selectedBooking.created_at)}
            />

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Left */}
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Booking Information
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Visit Date</p>
                      <p className="mt-1">{formatDate(selectedBooking.visit_date)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Visitors</p>
                      <p className="mt-1">{selectedBooking.total_count || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Total Amount</p>
                      <p className="mt-1">{formatCurrency(selectedBooking.total_amount)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Discount</p>
                      <p className="mt-1">
                        {formatCurrency(selectedBooking.discount_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Paid Amount</p>
                      <p className="mt-1">{formatCurrency(selectedBooking.total_paid)}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Balance</p>
                      <p className="mt-1">{formatCurrency(selectedBooking.balance)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      Payment Details
                    </h3>
                    {selectedBookingPayment?.status && (
                      <span className="text-xs font-semibold px-2 py-1 rounded-full bg-gray-100 text-gray-700 capitalize">
                        {selectedBookingPayment.status}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <InfoRow label="Amount" value={formatCurrency(selectedBookingPayment?.amount)} />
                      <InfoRow label="Payment method" value={selectedBookingPayment?.payment_method} />
                      <InfoRow label="Order ID" value={selectedBookingPayment?.order_id} />
                    </div>
                    <div className="space-y-2">
                      <InfoRow label="Created date" value={formatDate(selectedBookingPayment?.created_at)} />
                      <InfoRow label="Created time" value={formatTime(selectedBookingPayment?.created_at)} />
                      <InfoRow label="Payment ID" value={selectedBookingPayment?.payment_id || selectedBookingPayment?.razorpay_payment_id} />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Tickets
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead>
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            Ticket Type
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            Qty
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            Unit Price
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">
                            Total
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {selectedBookingTickets.length > 0 ? (
                          selectedBookingTickets.map((t, idx) => (
                            <tr key={idx}>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {t?.attraction_ticket_type
                                  ?.attraction_ticket_type_master?.name || "Ticket"}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {safeText(t.quantity, "0")}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {formatCurrency(t.unit_price)}
                              </td>
                              <td className="px-4 py-2 text-sm text-gray-900">
                                {formatCurrency(t.total_price)}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-8 text-center text-sm text-gray-500"
                            >
                              No ticket details available for this booking.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-6">
                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Customer
                  </h3>
                  <div className="space-y-2">
                    <InfoRow label="Name" value={selectedBooking.user?.name} />
                    <InfoRow label="Email" value={selectedBooking.user?.email} />
                    <InfoRow label="Phone" value={selectedBooking.user?.phone} />
                    <InfoRow label="User ID" value={selectedBooking.user?.id} />
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg border">
                  <h3 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-3">
                    Attraction
                  </h3>
                  <div className="space-y-2">
                    <InfoRow label="Name" value={selectedBooking.attraction?.name} />
                    <InfoRow label="Location" value={selectedBooking.attraction?.location} />
                    <InfoRow label="Attraction ID" value={selectedBooking.attraction?.id} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Dialog>
      )}

      {/* Pagination */}
      <div className="mt-4">
        <Pagination
          pagination={pagination}
          onPageChange={handlePageChange}
          showInfo={true}
        />
      </div>
    </>
  );
};

export default AttractionBookings;

